import { useEffect, useMemo } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import LazyHydrate from "react-lazy-hydration";
import Breadcrumb from "components/Breadcrumb/Breadcrumb";
import ProductCard from "components/ProductCard/ProductCard";
import { getLocalCurrency } from "functions/currency";
import { getAccessorieName } from "functions/getProductName";
import useWindowSize from "hooks/useWindowSize";
import { addDataLayer } from "lib/ga";
import CategoryBanner, { CategoryBannerProps } from "components/CategoryBanner/CategoryBanner";
import { getSeoContentByCategorySlug, getSeoTitleByCategorySlug } from "functions/getSeoContentByCategorySlug";
import { SeoBlock, CategoryBannerImage } from "constants/dataTypes";
import CategorySeoText from "components/Others/CategorySeoText";
import FAQJson from "components/Others/FAQJson";
import ProductCardwLensColour from "components/ProductCard/ProductCardwLensColour";
import React from "react";
import CategoryBannerImageComponent from "components/CategoryBannerImage/CategoryBannerImage";

const RecommendedProductsSlider = dynamic(
	() => import('components/Others/RecommendedProductsSlider')
);

interface AccessoryProduct {
	name: string;
	price: number;
	regular_price: number;
	category: string;
	list: string;
	position: number;
	images: string | null;
	href: string;
	priority: boolean;
	slug: string;
};

interface CategoryInfo {
	name: string;
	slug: string;
}

export default function CategoryComponent({
	categoryInfo,
	categoryBannerProps,
	products,
	seoBlock,
	categoryBannerImages
}: {
	categoryInfo: CategoryInfo;
	categoryBannerProps: CategoryBannerProps;
	products: any[];
	seoBlock: SeoBlock;
	categoryBannerImages: CategoryBannerImage[];
}) {
	const { width } = useWindowSize();
	const router = useRouter();

	const data = [
		{
			id: 1,
			href: '',
			text: 'Dresden Vision'
		},
		{
			id: 2,
			href: 'shop',
			text: 'Shop'
		},
		{
			id: 3,
			href: `shop/accessories`,
			text: 'Accessories'
		},
		{
			id: 4,
			href: `shop/accessories/${categoryInfo!.slug}`,
			text: `${categoryInfo!.name}`
		}
	];

	// Create an indexed map of banners
	const indexedBanners = useMemo(() => {
		const bannerMap = new Map<number, CategoryBannerImage>();
		
		categoryBannerImages.forEach(banner => {
			if (banner.bannerType === 'small-square') {
				bannerMap.set(banner.orderIndex, banner);
			} else {
				// For large rectangles, calculate the next multiple of 3
				const nextMultipleOfThree = Math.ceil(banner.orderIndex / 3) * 3 + 1;
				bannerMap.set(nextMultipleOfThree, banner);
			}
		});
		
		return bannerMap;
	}, [categoryBannerImages]);

	const accessoryProducts: any[] = useMemo(() => (
		[...products].map((variationNode, index) => (
			{
				name: variationNode.name,
				price: variationNode.meta.price,
				regular_price: variationNode.meta.regular_price,
				category: variationNode.name,
				slug: variationNode.slug,
				list: 'Category',
				position: index + 1,
				href: `/shop/accessories/${variationNode.slug}`,
				priority: width > 600 ? index <= 1 : index == 0
			} as AccessoryProduct
		)).flat()

	), [products, width]);

	useEffect(() => {

		if (accessoryProducts == null) {
			return;
		}

		addDataLayer({
			event: 'ImpCompleted',
			ecommerce: {
				currencyCode: getLocalCurrency(router.locale),
				impressions: accessoryProducts.map(accessoryProduct => (
					{
						name: accessoryProduct.name,
						price: accessoryProduct.price,
						category: accessoryProduct.category,
						list: accessoryProduct.list,
						position: accessoryProduct.position
					}
				))
			}
		});

	}, [router, accessoryProducts]);

	if (router.isFallback) {
		return (
			<div className="flex h-[70vh] items-center justify-center">
				Loading...
			</div>
		);
	}

	// Track the number of category banner images
	let categoryBannerImageCount = 0;

	return (
		<div>
			<Head>
				<title>{`${getSeoTitleByCategorySlug(categoryInfo.slug)} | Dresden Vision`}</title>
				<meta name="twitter:title" content={`${getSeoTitleByCategorySlug(categoryInfo.slug)} | Dresden Vision`} />
				<meta name="og:title" content={`${getSeoTitleByCategorySlug(categoryInfo.slug)} | Dresden Vision`} />
				<meta
					name="description"
					content={getSeoContentByCategorySlug(categoryInfo.slug)}
				/>
				<meta
					property="og:description"
					content={getSeoContentByCategorySlug(categoryInfo.slug)}
				/>
				<meta
					name="twitter:description"
					content={getSeoContentByCategorySlug(categoryInfo.slug)}
				/>
			</Head>
			<CategoryBanner {...categoryBannerProps} breadcrumbItems={data} />
			<div className="mx-auto max-w-[1224px] bg-white px-4 pt-4">
				<div className="mt-[24px] grid grid-cols-1 gap-y-[20px] md:grid-cols-2 md:gap-x-[15px] md:gap-y-[30px] shop:grid-cols-3">
					{accessoryProducts.map((accessoryProduct, index) => {
						const currentPosition = index + 1 + categoryBannerImageCount;
						const bannerImage = indexedBanners.get(currentPosition);
						if(bannerImage) {
							if(bannerImage.bannerType === 'large-rectangle') {
								categoryBannerImageCount+=3;
							} else {
								categoryBannerImageCount++;
							}
						}
						
						return (
							<React.Fragment key={`product-${index}`}>
								{bannerImage && (
									<div className={bannerImage.bannerType === 'large-rectangle' ? 'col-span-full' : undefined}>
										<CategoryBannerImageComponent banner={bannerImage} width={width} />
									</div>
								)}
								<LazyHydrate whenVisible key={`card-${index}`}>
									<ProductCardwLensColour
										onClickPoroduct={() => {
											addDataLayer({
												event: 'productClick',
												ecommerce: {
													click: {
														actionField: { list: 'category' },
														products: [
															{
																name: getAccessorieName(accessoryProduct.name),
																price: accessoryProduct.price,
																category: accessoryProduct.category,
																list: accessoryProduct.list,
																position: accessoryProduct.position
															}
														]
													}
												}
											})
										}}
										width={width}
										data={{
											images: [`${accessoryProduct.images}`],
											title: getAccessorieName(accessoryProduct.name),
											price: accessoryProduct.price,
											href: accessoryProduct.href,
											prio: accessoryProduct.priority,
											regularPrice: accessoryProduct.regular_price,
											slug: accessoryProduct.slug
										}}
									/>
								</LazyHydrate>
							</React.Fragment>
						);
					})}
				</div>
				<LazyHydrate whenVisible>
					<RecommendedProductsSlider />
				</LazyHydrate>
				<LazyHydrate whenVisible>
					<CategorySeoText seoBlock={seoBlock} />
				</LazyHydrate>
				<FAQJson faq={seoBlock.faq} />
			</div>
		</div>
	)
}